import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader, StatusBadge, Empty } from "@/components/shared";
import NewCustomerDialog from "@/components/NewCustomerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtNOK, CUSTOMER_STATUS, KYC_STATUS, RISK } from "@/lib/labels";
import { Plus, Users } from "lucide-react";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const nav = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    api.get(`/customers?${params}`).then((r) => setRows(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, status]);

  return (
    <Page>
      <PageHeader title="Kunder" subtitle="Alle leads, prospekter og aktive kunder" testid="customers-header">
        <NewCustomerDialog defaultStatus="onboarding" onCreated={load}
          trigger={<Button data-testid="new-customer-btn" className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Ny kunde</Button>} />
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-4">
        <Input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk navn eller org.nr…" className="max-w-xs bg-secondary border-border" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="status-filter" className="w-52 bg-secondary border-border"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Alle statuser</SelectItem>
            {Object.entries(CUSTOMER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["Kunde", "Org.nr", "Status", "MRR", "ARR", "Risiko", "KYC", "Avdeling", "Lokasjon"].map((h) => (
                  <th key={h} className="px-4 py-3 label-caps font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => nav(`/kunder/${c.id}`)}
                  className="border-b border-border/50 hover:bg-accent/50 cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-medium">{c.legal_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.org_number || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge map={CUSTOMER_STATUS} value={c.status} /></td>
                  <td className="px-4 py-3 font-medium">{fmtNOK(c.mrr)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtNOK(c.arr)}</td>
                  <td className="px-4 py-3">{c.risk_level ? <StatusBadge map={RISK} value={c.risk_level} /> : "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{KYC_STATUS[c.kyc_status] || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{(c.departments && c.departments.length ? c.departments.join(", ") : c.department) || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.location || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <Empty icon={Users} title="Ingen kunder" hint="Opprett din første kunde eller lead." />}
      </div>
    </Page>
  );
}
